"use client";

import React, { useState } from "react";
import { X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogCloseButton } from "@shared/src/components/ui/dialog";
import { Button } from "@shared/src/components/ui/button";
import { Progress } from "@shared/src/components/ui/progress";
import { ConfirmationDialog } from "@shared/src/components/common/dialogs/ConfirmationDialog";
import { OrderHeader } from "@shared/src/components/modules/orders/components/OrderHeader";
import { OrderDetails } from "@shared/src/components/modules/orders/components/OrderDetails";
import { PaymentMethodSection } from "@shared/src/components/modules/orders/components/PaymentMethodSection";
import { AdvertisersTerms } from "@shared/src/components/modules/orders/components/AdvertisersTerms";
import { QuickTrade } from "@sdk/shared/types/order.types";
import { EnrichedAd } from "@sdk/shared/types/ads.types";
import { paymentMethods } from "@shared/src/config/payment-methods";
import { timeLeftFromDate } from "@shared/src/utils/time";
import { CountdownTimer } from "@shared/src/components/common/misc/CountdownTimer";
import { getSdk } from "@web/app/lib/sdkClient";
import { ixToast } from "@shared/src/utils/ixToast";
import { useHostStore } from "@shared/src/stores/useHostStore";
import { useNotificationStore } from "@shared/src/stores/useNotificationStore";
import { useCountdown } from "@shared/src/utils/helpers/countdown";
import { Spinner } from "../misc/Spinner";
import { ProcessedMerchantStats } from "@sdk/shared/lib/merchant-stats";
import { ProgressWithTimer } from "../misc/ProgressWithTimer";
import { useQuickTradeOptions } from "@shared/src/hooks/quick-trade/useQuickTradeOptions";

interface IncomingOrderDialogProps {
  open: boolean;
  onOpenChange?: (open: boolean) => void;
  onAccept: () => Promise<void>;
  onReject: () => void;
  quickTrade: QuickTrade;
  ad: EnrichedAd;
  isLoading: boolean;
  takerStats: ProcessedMerchantStats;
}

export function IncomingOrderDialog({
  open,
  onOpenChange,
  onAccept,
  onReject,
  quickTrade,
  ad,
  isLoading,
  takerStats,
}: IncomingOrderDialogProps) {
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const setQuickTradeNotification = useNotificationStore(state => state.setQuickTradeNotification);
  const handleReject = () => {
    setShowRejectDialog(true);
  };

  const handleAccept = async () => {
    setLoading(true);
    await onAccept();
    setLoading(false);
  };

  const handleExpired = () => {
    ixToast.error("The order has expired", { title: "Order Expired" });
    setQuickTradeNotification(null);
  };

  const handleConfirmReject = () => {
    setShowRejectDialog(false);
    onReject();
  };
  // Defensive fallbacks for prop-driven components
  // If tradeType is not present, infer from orderData.type or default to 'buy'
  const tradeType = quickTrade?.type === "buy" ? "sell" : "buy";
  const merchantAddress = ad.merchantAddress || "";
  const token = ad.tokenDetails || { symbol: "" };
  const traderNetworkId = quickTrade.traderChain;
  const merchantNetworkId = ad.networkId;
  const currencySymbol = quickTrade.currency;
  const price = ad.price;
  const quantity = quickTrade.quantity;
  const paymentMethod = quickTrade.paymentMethod;
  const transactionId = quickTrade.id;
  const sdk = useHostStore(state => state.sdk);
  const { quickTradeWaitTime = 0 } = useQuickTradeOptions();
  const expiryTime = new Date(quickTrade.lastContactedTime + quickTradeWaitTime);

  console.log({ quickTrade, ad, quickTradeWaitTime });

  return (
    <>
      <Dialog open={open || isLoading} onOpenChange={onOpenChange}>
        <DialogContent
          size="lg"
          background="primary"
          padding="md"
          position="center-mobile-bottom"
          hideCloseButton={true}
          scroll={true}
          className="gap-6 max-h-[95vh]"
        >
          {isLoading ? (
            <Spinner size={48} className="m-auto my-16" />
          ) : (
            <>
              <DialogHeader className="pb-0">
                <div className="flex justify-between">
                  <DialogTitle className="sr-only">Merchant Stake</DialogTitle>
                  <div />
                  <div className="flex w-full items-center justify-center gap-2">
                    <h6 className="text-center text-text-primary">Incoming Order</h6>
                  </div>
                </div>
              </DialogHeader>
              <div className="space-y-2">
                {/* Header */}
                <OrderHeader
                  tradeType={tradeType}
                  username={takerStats.username}
                  orderStats={{ ...takerStats, timeLimit: ad.timeLimit }}
                  actorAddress={merchantAddress}
                />
                {/* Order Details */}
                <OrderDetails
                  tradeType={tradeType}
                  tokenSymbol={token.symbol}
                  traderNetworkId={traderNetworkId}
                  merchantNetworkId={merchantNetworkId}
                  currencySymbol={currencySymbol}
                  price={price}
                  quantity={quantity}
                />
                {/* Payment Method */}
                <PaymentMethodSection
                  paymentMethod={{
                    name: paymentMethod,
                    color: paymentMethods.find(method => method.value === paymentMethod)?.color || "",
                  }}
                  transactionId={transactionId}
                />
                {/* Advertisers Terms */}
                <AdvertisersTerms message={ad.message} tags={ad.tags} />
              </div>
              <div className="px-4 flex flex-col gap-2">
                <div className="flex mb-3 flex-col items-center justify-center gap-2">
                  <p>Waiting for you to respond...</p>
                  <CountdownTimer
                    onComplete={handleExpired}
                    className="py-2 px-4 rounded-2xl bg-surfaces-bg-secondary w-fit"
                    targetDate={expiryTime}
                  />
                  <ProgressWithTimer targetDate={expiryTime} totalTime={quickTradeWaitTime} />
                </div>
                <div className="flex flex-row">
                  <Button
                    variant="secondary"
                    size="md"
                    radius="full"
                    width="flex"
                    className="text-small"
                    onClick={handleReject}
                    disabled={loading}
                  >
                    Reject
                  </Button>
                  <Button
                    onClick={handleAccept}
                    variant="primary"
                    size="md"
                    radius="full"
                    width="flex"
                    className="text-small"
                    loading={loading}
                  >
                    Accept
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
      <ConfirmationDialog
        open={showRejectDialog}
        onOpenChange={setShowRejectDialog}
        title="Confirm Rejection"
        description="Are you sure you want to reject this order? This action cannot be undone."
        icon={
          <div className="bg-surfaces-bg-secondary-darker rounded-full p-3">
            <X className="h-8 w-8 text-icons-icon-error" />
          </div>
        }
        confirmButton={{
          label: "Reject Order",
          onClick: handleConfirmReject,
          variant: "danger",
        }}
        cancelButton={{
          label: "Cancel",
          onClick: () => setShowRejectDialog(false),
        }}
      />
    </>
  );
}


